import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { StatCard, Card, PageHeader, StatusBadge, StatsGrid } from '../components/UI';
import { IconUsers, IconWallet, IconTx, IconDevice, IconX, IconSub } from '../components/Icons';
import { fmt } from '../utils/helpers';

function Loader() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'50vh', flexDirection:'column', gap:12, color:'var(--text3)' }}>
      <div style={{ width:24, height:24, border:'2px solid var(--border2)', borderTopColor:'var(--accent)', borderRadius:'50%', animation:'spin 0.6s linear infinite' }} />
      <span style={{ fontSize:13 }}>Loading dashboard…</span>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.getDashboard(), api.getRevenueAnalytics('30d')])
      .then(([d, r]) => { setData(d); setRevenue(r); })
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (!data) return null;

  const { stats, recentTransactions, recentUsers } = data;
  const chartData = revenue?.daily?.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-NG', { day:'2-digit', month:'short' }),
    revenue: parseFloat(d.revenue || 0),
  })) || [];

  const h = new Date().getHours();
  const greeting = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';

  return (
    <div className="page-enter">
      <PageHeader title="Dashboard" subtitle={`Good ${greeting}, here's your overview.`} />

      <StatsGrid>
        <StatCard label="Total Users"   value={stats.totalUsers?.toLocaleString()}         sub={`+${stats.newUsersToday} today`}            icon={IconUsers}  accent="var(--accent)" />
        <StatCard label="Active Subs"   value={stats.activeSubscriptions?.toLocaleString()} sub="Across all plans"                           icon={IconSub}    accent="var(--green)" />
        <StatCard label="Revenue Today" value={fmt.naira(stats.revenueToday)}               sub={`${fmt.naira(stats.revenueThisMonth)} / mo`} icon={IconWallet} accent="var(--yellow)" />
        <StatCard label="Online"        value={stats.onlineDevices?.toLocaleString()}        sub="Devices online now"                         icon={IconDevice} accent="var(--purple)" />
        <StatCard label="Failed Txns"   value={(stats.failedTransactions||0).toLocaleString()} sub={`${stats.pendingTransactions||0} pending`} icon={IconX}    accent="var(--red)" />
      </StatsGrid>

      {/* Revenue chart */}
      <Card style={{ marginBottom:20, padding:'18px 16px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:8 }}>
          <div>
            <h3 style={{ fontSize:14, fontWeight:700 }}>Revenue — Last 30 Days</h3>
            <span style={{ fontSize:12, color:'var(--text3)' }}>Total: <b style={{ color:'var(--text)' }}>{fmt.naira(revenue?.summary?.total_revenue)}</b></span>
          </div>
          <button onClick={() => navigate('/analytics')} style={{ fontSize:12, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>Full analytics →</button>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#4f8ef7" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#4f8ef7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fontSize:10, fill:'var(--text3)' }} axisLine={false} tickLine={false} interval={4} />
            <Tooltip contentStyle={{ background:'var(--surface2)', border:'1px solid var(--border2)', borderRadius:8, fontSize:12 }} formatter={v => [fmt.naira(v), 'Revenue']} />
            <Area type="monotone" dataKey="revenue" stroke="#4f8ef7" strokeWidth={2} fill="url(#rev)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Recent data - stack on mobile */}
      <div className="dash-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        <Card>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={{ fontSize:13, fontWeight:700 }}>Recent Transactions</h3>
            <button onClick={() => navigate('/transactions')} style={{ fontSize:11, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>View all</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {recentTransactions?.slice(0,6).map((tx,i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.User?.name || '—'}</div>
                  <div style={{ fontSize:10, color:'var(--text3)', marginTop:1 }}>{fmt.relative(tx.createdAt)}</div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color: tx.type==='topup' ? 'var(--green)' : 'var(--text2)' }}>
                    {tx.type==='topup' ? '+' : '−'}{fmt.naira(tx.amount)}
                  </div>
                  <StatusBadge status={tx.status} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={{ fontSize:13, fontWeight:700 }}>New Users</h3>
            <button onClick={() => navigate('/users')} style={{ fontSize:11, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>View all</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {recentUsers?.map(u => (
              <div key={u.id} style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:30, height:30, borderRadius:'50%', background:'var(--accent-dim)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'var(--accent)', flexShrink:0 }}>
                  {u.name?.[0]?.toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name}</div>
                  <div style={{ fontSize:10, color:'var(--text3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
                </div>
                <StatusBadge status={u.is_active ? 'active' : 'inactive'} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .dash-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}